import React from 'react'
import { css } from '@emotion/react'
import { color } from "@/styles/theme";

type Props = Omit<JSX.IntrinsicElements['input'], 'size'> & {
  label?: string
  isError?: boolean
  bgSize?: 'md' | 'sm'
  onClick?: (e: React.MouseEvent<HTMLElement>) => void
}

const InnerCheckBox = (
  props: Props,
  ref: React.ForwardedRef<HTMLInputElement>,
) => {
  const {
    isError,
    className,
    label,
    width,
    bgSize = 'md',
    onClick,
    ...inputProps
  } = props

  const checkStyle = generateStyle({
    checked: props.checked,
    disabled: props.disabled,
    isError: isError,
  })

  return (
    <label
      css={[{ ...container }, { width }]}
      className={className}
      onClick={onClick}
    >
      <div
        css={[
          checkContainerBase,
          checkContainerStyles.checked.get(!!props.checked),
          checkContainerStyles.disabled.get(!!props.disabled),
          checkContainerStyles.bgSizes[bgSize],
        ]}
      >
        <span css={checkStyle} />
        <input type="checkbox" css={input} {...inputProps} ref={ref} />
      </div>
      <span css={labelStyles.disabled.get(!!props.disabled)}>{label}</span>
    </label>
  )
}

export const CheckBox = React.forwardRef(InnerCheckBox)

const generateStyle = (state: {
  checked?: boolean
  disabled?: boolean
  isError?: boolean
}) => {
  if (state.checked && state.isError) return checked
  if (state.checked && state.disabled) return checkedDisable
  if (state.checked) return checked
  if (state.disabled) return uncheckedDisable
  return unChecked
}

const container = css({
  display: 'inline-flex',
  alignItems: 'center',
  verticalAlign: 'middle',
  gap: 7,
})

const checkContainerBase = css({
  position: 'relative',
  display: 'inline-flex',
  flexGrow: 0,
  flexShrink: 0,
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'transparent',
  borderRadius: '50%',
  '&:hover': {
    '+ span': {
      zIndex: 1,
    },

    '&:before': {
      content: '""',
      position: 'absolute',
      borderRadius: '50%',
      backgroundColor: color.gray100,
    },
  },
})

const checkContainerStyles = {
  checked: new Map([
    [
      true,
      css({
        '&:focus-within': {
          backgroundColor: color.gray100,
          outline: 0,
        },
      }),
    ],
  ]),
  disabled: new Map([
    [
      true,
      css({
        '&:hover': {
          '&:after': {
            backgroundColor: 'transparent',
          },
        },
      }),
    ],
  ]),
  bgSizes: {
    md: css({
      '&:hover': {
        '&:before': {
          width: 40,
          height: 40,
        },
      },
    }),
    sm: css({
      '&:hover': {
        '&:before': {
          width: 32,
          height: 32,
        },
      },
    }),
  },
}

const input = css({
  position: 'absolute',
  margin: 0,
  padding: 0,
  opacity: 0,
  cursor: 'pointer',
  height: 0,
  width: 0,
})

const base = css({
  boxSizing: 'border-box',
  position: 'relative',
  display: 'inline-block',
  width: 18,
  height: 18,
  borderWidth: 2,
  borderStyle: 'solid',
  borderColor: color.gray600,
  borderRadius: 3,
  cursor: 'pointer',

  '&:before': {
    content: '""',
    position: 'absolute',
    top: '15%',
    left: '50%',
    width: 10,
    height: 5,
    borderStyle: 'solid',
    borderLeft: `2px solid ${color.white}`,
    borderBottom: `2px solid ${color.white}`,
    borderColor: 'transparent',
    transform: 'rotate(-45deg) translate(-50%, -50%)',
  },
})

const unChecked = base

const uncheckedDisable = css(unChecked, {
  borderColor: color.gray300,
  cursor: 'default',
  '&:before': {
    borderColor: 'transparent',
  },
})

const checked = css(base, {
  borderColor: color.primary,
  backgroundColor: color.primary,
  '&:before': {
    borderColor: color.white,
  },
})

const checkedDisable = css(checked, {
  backgroundColor: color.gray300,
  borderColor: color.gray300,
  cursor: 'default',
  '&:before': {
    borderColor: color.white,
  },
})

const labelBaseStyle = css({
  width: '100%',
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const labelStyles = {
  disabled: new Map([
    [true, css(labelBaseStyle, { color: color.gray600 })],
    [false, css(labelBaseStyle, { cursor: 'pointer' })],
  ]),
}